import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../hooks/useTenant';
import NoiRockLogo from '../../components/NoiRockLogo';

const PrivacyPolicy = () => {
  const tenant = useTenant();
  const isTcn = (tenant?.effectiveTenantSlug ?? tenant?.tenantSlug) === 'tcn';
  const brandName = isTcn ? 'TCN Agent' : 'NoiRock';
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-bg)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, var(--color-primary) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* Header — tenant-aware (TCN logo + name on tcn.noirock.app, NoiRock otherwise).
          Back arrow + brand both route to /login-authentication (stays on the current
          tenant domain), so a user reading Privacy on tcn.noirock.app returns to the
          TCN login page rather than to a tenant-less home. */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-3">
          <Link to="/login-authentication" aria-label="Back to login" className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 no-underline hover:bg-white/10 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </Link>
          <Link to="/login-authentication" className="flex items-center gap-2 no-underline">
            <NoiRockLogo size={28} />
            <span className="text-white/60 text-xs font-semibold tracking-wide">{brandName}</span>
          </Link>
        </div>
        <Link
          to="/terms"
          className="text-white/40 text-xs hover:text-white/70 transition-colors no-underline"
        >
          Terms of Service →
        </Link>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <div className="mb-10">
            <h1
              className="text-2xl font-bold text-white tracking-tight mb-2"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Privacy Policy
            </h1>
            <p className="text-white/30 text-sm">Last updated: March 20, 2026</p>
          </div>

          <div className="space-y-8 text-white/60 text-sm leading-relaxed">

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                1. Data Controller
              </h2>
              <p>
                NoiRock ("the Agency") is the data controller for personal data processed 
                through NoiRock (noirock.app). For any privacy-related inquiries, contact us 
                at: <span className="text-white/80">contact@noirock.app</span>
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                2. Data We Collect
              </h2>
              <p>We collect the following categories of personal data:</p>

              <div className="mt-4 rounded-xl border border-white/8 overflow-hidden" style={{ backgroundColor: '#111111' }}>
                <div className="px-4 py-3 border-b border-white/5">
                  <p className="text-white/80 text-xs font-semibold uppercase tracking-wider">Via TikTok Login Kit</p>
                </div>
                <div className="px-4 py-3 space-y-1">
                  <p className="text-white/50 text-sm">• TikTok unique identifiers (open_id, union_id)</p>
                  <p className="text-white/50 text-sm">• Display name and profile avatar URL</p>
                  <p className="text-white/50 text-sm">• TikTok username</p>
                  <p className="text-white/50 text-sm">• Authentication tokens (access_token, refresh_token)</p>
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-white/8 overflow-hidden" style={{ backgroundColor: '#111111' }}>
                <div className="px-4 py-3 border-b border-white/5">
                  <p className="text-white/80 text-xs font-semibold uppercase tracking-wider">Via Agency Operations</p>
                </div>
                <div className="px-4 py-3 space-y-1">
                  <p className="text-white/50 text-sm">• TikTok LIVE performance data (diamonds, valid days, rankings)</p>
                  <p className="text-white/50 text-sm">• Manager assignment and group affiliation</p>
                  <p className="text-white/50 text-sm">• Commission and payment records</p>
                  <p className="text-white/50 text-sm">• Platform usage logs and last login timestamps</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                3. How We Use Your Data
              </h2>
              <p>Your personal data is used for the following purposes:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/50">
                <li>Authenticating your identity and verifying your affiliation with the Agency</li>
                <li>Providing access to your performance dashboard and agency resources</li>
                <li>Calculating commissions, bonuses, and payroll</li>
                <li>Communicating performance updates and agency notifications</li>
                <li>Improving the platform and resolving technical issues</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                4. Data Storage & Security
              </h2>
              <p>
                Your data is stored securely on Supabase (PostgreSQL) with the following measures:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/50">
                <li>All data is encrypted in transit (TLS/HTTPS) and at rest</li>
                <li>TikTok authentication tokens are stored encrypted using server-side encryption</li>
                <li>Access to the database is restricted to authorized personnel only</li>
                <li>Row-Level Security (RLS) policies ensure users can only access their own data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                5. Data Sharing
              </h2>
              <p>
                We do not sell, rent, or share your personal data with third parties for marketing 
                purposes. Your data may be shared only in the following circumstances:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/50">
                <li>With your assigned manager for performance management purposes</li>
                <li>With infrastructure providers (Supabase, Vercel) for hosting and technical operations</li>
                <li>When required by applicable law or legal proceedings</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                6. Data Retention
              </h2>
              <p>
                Your personal data is retained for the duration of your affiliation with the Agency. 
                Upon termination of your contract, your data will be retained for a maximum of 12 months 
                for accounting and legal compliance purposes, after which it will be permanently deleted.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                7. Your Rights
              </h2>
              <p>You have the following rights regarding your personal data:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/50">
                <li><span className="text-white/70">Access:</span> Request a copy of the data we hold about you</li>
                <li><span className="text-white/70">Rectification:</span> Request correction of inaccurate data</li>
                <li><span className="text-white/70">Deletion:</span> Request deletion of your data (subject to legal obligations)</li>
                <li><span className="text-white/70">Revocation:</span> Revoke TikTok authentication access at any time via your TikTok settings</li>
                <li><span className="text-white/70">Portability:</span> Request an export of your data in a structured format</li>
              </ul>
              <p className="mt-2">
                To exercise any of these rights, contact us at:{' '}
                <span className="text-white/80">contact@noirock.app</span>
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                8. Cookies & Tracking
              </h2>
              <p>
                NoiRock uses essential session cookies for authentication purposes only. We do not use 
                advertising cookies, tracking pixels, or analytics tools that collect personal data.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                9. Changes to This Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be reflected on 
                this page with an updated revision date. Continued use of the platform after changes 
                constitutes acceptance of the revised policy.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                10. Contact
              </h2>
              <p>
                For any questions or concerns about this Privacy Policy or our data practices, 
                please contact: <span className="text-white/80">contact@noirock.app</span>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-6 border-t border-white/5 text-center">
            <p className="text-white/20 text-xs">
              © 2026 {brandName} · v2.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
